import { useState } from "react";
import { Button } from "@/components/_components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/_components/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Save, Clock, Globe } from "lucide-react";
import toast from "react-hot-toast";
import * as api from "@/lib/api/client";
import type { ComplianceCountryDto, ComplianceCountryCreateRequest, ComplianceCountryUpdateRequest } from "@/lib/api/types";
import { Checkbox } from "@/components/_components/checkbox";
import { useErrorHandler } from "@/hooks/useErrorHandler";

interface CreateComplianceCountryDialogProps {
  country?: ComplianceCountryDto;
  onSuccess?: () => void;
  trigger?: React.ReactNode;
}

const WEEKDAYS = [
  { value: 'MONDAY', label: 'Monday' },
  { value: 'TUESDAY', label: 'Tuesday' },
  { value: 'WEDNESDAY', label: 'Wednesday' },
  { value: 'THURSDAY', label: 'Thursday' },
  { value: 'FRIDAY', label: 'Friday' },
  { value: 'SATURDAY', label: 'Saturday' },
  { value: 'SUNDAY', label: 'Sunday' },
];

const CreateComplianceCountryDialog: React.FC<CreateComplianceCountryDialogProps> = ({
  country,
  onSuccess,
  trigger
}) => {
  const { handleError } = useErrorHandler();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    countryCode: country?.countryCode || '',
    countryName: country?.countryName || '',
    isdCode: country?.isdCode || '',
    startWorkingHour: country?.startWorkingHour || '08:00:00',
    endWorkingHour: country?.endWorkingHour || '21:00:00',
    weekends: country?.weekends ? country.weekends.split(',').map(w => w.trim()) : ['SATURDAY', 'SUNDAY'],
    language: country?.language || '',
  });

  const isEditing = !!country;

  const handleInputChange = (field: string, value: string | string[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleWeekendChange = (day: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      weekends: checked 
        ? [...prev.weekends, day]
        : prev.weekends.filter(w => w !== day)
    }));
  };

  const validateForm = () => {
    if (!formData.countryCode.trim()) {
      toast.error("Country code is required");
      return false;
    }
    if (!formData.countryName.trim()) {
      toast.error("Country name is required");
      return false;
    }
    if (!formData.isdCode.trim()) {
      toast.error("ISD code is required");
      return false;
    }
    if (!formData.startWorkingHour) {
      toast.error("Start working hour is required");
      return false;
    }
    if (!formData.endWorkingHour) {
      toast.error("End working hour is required");
      return false;
    }
    if (formData.startWorkingHour >= formData.endWorkingHour) {
      toast.error("Start working hour must be before end working hour");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setIsLoading(true);

      const requestData = {
        countryCode: formData.countryCode.trim().toUpperCase(),
        countryName: formData.countryName.trim(),
        isdCode: formData.isdCode.trim(),
        startWorkingHour: formData.startWorkingHour,
        endWorkingHour: formData.endWorkingHour,
        weekends: formData.weekends.join(','),
        language: formData.language.trim() || undefined,
      };

      if (isEditing && country) {
        const updateData: ComplianceCountryUpdateRequest = {
          ...requestData,
          id: country.id!,
          version: country.version || 1,
        };
        await api.updateComplianceCountry(country.id!, updateData);
        toast.success("Country updated successfully");
      } else {
        const createData: ComplianceCountryCreateRequest = requestData;
        await api.createComplianceCountry(createData);
        toast.success("Country created successfully");
      }

      setOpen(false);
      onSuccess?.();
      
      // Reset form if creating new
      if (!isEditing) {
        setFormData({
          countryCode: '',
          countryName: '',
          isdCode: '',
          startWorkingHour: '08:00:00',
          endWorkingHour: '21:00:00',
          weekends: ['SATURDAY', 'SUNDAY'],
          language: '',
        });
      }
    } catch (error: unknown) {
      // Use centralized error handler to display appropriate error message
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const defaultTrigger = (
    <Button className="h-9 px-4 bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transition-all duration-200 font-semibold text-sm rounded-md">
      <Plus className="h-4 w-4 mr-1.5" />
      Add Country
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || defaultTrigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-primary" />
            {isEditing ? 'Edit Country' : 'Add New Country'}
          </DialogTitle>
          <DialogDescription>
            {isEditing 
              ? 'Update the country compliance and quiet hours settings.'
              : 'Configure compliance and quiet hours settings for a new country.'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Country Code */}
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="countryCode" className="text-right font-semibold">
              Country Code <span className="text-destructive">*</span>
            </Label>
            <Input
              id="countryCode"
              placeholder="e.g., USA"
              value={formData.countryCode}
              onChange={(e) => handleInputChange('countryCode', e.target.value.toUpperCase())}
              className="col-span-3"
              maxLength={3}
            />
          </div>

          {/* Country Name */}
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="countryName" className="text-right font-semibold">
              Country Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="countryName"
              placeholder="e.g., United States"
              value={formData.countryName}
              onChange={(e) => handleInputChange('countryName', e.target.value)}
              className="col-span-3"
            />
          </div>

          {/* ISD Code */}
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="isdCode" className="text-right font-semibold">
              ISD Code <span className="text-destructive">*</span>
            </Label>
            <Input
              id="isdCode"
              placeholder="e.g., +1"
              value={formData.isdCode}
              onChange={(e) => handleInputChange('isdCode', e.target.value)}
              className="col-span-3"
            />
          </div>

          {/* Language */}
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="language" className="text-right font-semibold">
              Language
            </Label>
            <Input
              id="language"
              placeholder="e.g., English, Spanish, French"
              value={formData.language}
              onChange={(e) => handleInputChange('language', e.target.value)}
              className="col-span-3"
            />
          </div>

          {/* Working Hours */}
          <div className="grid grid-cols-4 items-center gap-4">
            <Label className="text-right font-semibold flex items-center gap-1">
              <Clock className="h-4 w-4" />
              Working Hours <span className="text-destructive">*</span>
            </Label>
            <div className="col-span-3 flex items-center gap-2">
              <Input
                type="time"
                value={formData.startWorkingHour.substring(0, 5)}
                onChange={(e) => handleInputChange('startWorkingHour', e.target.value + ':00')}
                className="flex-1"
              />
              <span className="text-muted-foreground">to</span>
              <Input
                type="time"
                value={formData.endWorkingHour.substring(0, 5)}
                onChange={(e) => handleInputChange('endWorkingHour', e.target.value + ':00')}
                className="flex-1"
              />
            </div>
          </div>

          {/* Weekends */}
          <div className="grid grid-cols-4 items-start gap-4">
            <Label className="text-right font-semibold pt-2">
              Weekends
            </Label>
            <div className="col-span-3 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                {WEEKDAYS.map((day) => (
                  <div key={day.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={day.value}
                      checked={formData.weekends.includes(day.value)}
                      onCheckedChange={(checked) => handleWeekendChange(day.value, checked as boolean)}
                    />
                    <Label htmlFor={day.value} className="text-sm font-normal">
                      {day.label}
                    </Label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Select the days that are considered weekends for this country
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? (
              <>
                <Save className="h-4 w-4 mr-2 animate-spin" />
                {isEditing ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {isEditing ? 'Update Country' : 'Create Country'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateComplianceCountryDialog;